import authService from './authService';
import { 
  RawMaterial, 
  RawMaterialFormData, 
  RawMaterialSearchModel 
} from '../types/rawMaterial';
import { PaginatedResponse, ApiResponse } from '../types/common';

class RawMaterialService {
  /**
   * Normalizes paginated API responses from the backend.
   * Handles multiple response shapes the Go backend may return:
   *   - { data: [...], pagination: { page, limit, total, totalPages } }
   *   - { data: [...], total, page, limit, total_pages }
   *   - { items: [...], total, page, limit, total_pages }
   *   - { data: { items: [...], ... }, ... }
   *   - [...] direct array
   */
  private static normalizePaginatedResponse(json: any): PaginatedResponse<RawMaterial> {
    let items: RawMaterial[] = [];
    let page = 1;
    let limit = 20;
    let total = 0;
    let totalPages = 1;

    if (Array.isArray(json)) {
      // Direct array
      items = json;
      total = json.length;
    } else if (json && typeof json === 'object') {
      // Extract items
      if (Array.isArray(json.data)) {
        items = json.data;
      } else if (json.data && Array.isArray(json.data.items)) {
        items = json.data.items;
      } else if (json.data && Array.isArray(json.data.data)) {
        items = json.data.data;
      } else if (Array.isArray(json.items)) {
        items = json.items;
      } else if (Array.isArray(json.raw_materials)) {
        items = json.raw_materials;
      }
      // else items stays []

      // Extract pagination — handles nested or flat, camelCase or snake_case
      const pag = json.pagination || json.data?.pagination || json;
      page       = pag.page        ?? page;
      limit      = pag.limit       ?? limit;
      total      = pag.total       ?? json.total ?? items.length;
      totalPages = pag.totalPages  ?? pag.total_pages ?? json.total_pages ?? Math.ceil(total / limit);
    }

    return {
      success: true,
      data: items,
      pagination: { page, limit, total, totalPages },
    };
  }

  /**
   * Get authenticated API client
   */
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * List raw materials with pagination and filters
   */
  async getRawMaterials(searchModel: RawMaterialSearchModel): Promise<PaginatedResponse<RawMaterial>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.searchCode && { code: searchModel.searchCode }),
      ...(searchModel.materialType !== undefined && { material_type: searchModel.materialType.toString() }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    if (searchModel.is_active !== undefined) {
      params.append('is_active', searchModel.is_active.toString());
    }
    if (searchModel.in_stock) {
      params.append('stock_status', 'in_stock');
    }
    if (searchModel.low_stock) {
      params.append('stock_status', 'low_stock');
    }
    if (searchModel.out_of_stock) {
      params.append('stock_status', 'out_of_stock');
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/raw-materials?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar matérias-primas');
    }

    const jsonResponse = await response.json();
    return RawMaterialService.normalizePaginatedResponse(jsonResponse);
  }

  /**
   * Search raw materials by term
   */
  async searchRawMaterials(
    query: string,
    page: number = 1,
    pageSize: number = 20,
    filters: any = {}
  ): Promise<{ rawMaterials: RawMaterial[]; pagination?: any }> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (filters.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }
    if (filters.materialType !== undefined) {
      params.append('material_type', filters.materialType.toString());
    }
    if (filters.in_stock) {
      params.append('stock_status', 'in_stock');
    }
    if (filters.low_stock) {
      params.append('stock_status', 'low_stock');
    }
    if (filters.out_of_stock) {
      params.append('stock_status', 'out_of_stock');
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/raw-materials/search?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar matérias-primas');
    }

    const jsonResponse = await response.json();

    // Handle different possible response structures
    let rawMaterialsArray: RawMaterial[] = [];
    let paginationData = null;

    if (Array.isArray(jsonResponse)) {
      rawMaterialsArray = jsonResponse;
    } else if (jsonResponse && typeof jsonResponse === 'object') {
      if (Array.isArray(jsonResponse.data)) {
        rawMaterialsArray = jsonResponse.data;
        paginationData = jsonResponse.pagination;
      } else if (jsonResponse.data && typeof jsonResponse.data === 'object' && Array.isArray(jsonResponse.data.data)) {
        rawMaterialsArray = jsonResponse.data.data;
        paginationData = jsonResponse.data.pagination;
      } else if (jsonResponse.data && typeof jsonResponse.data === 'object' && Array.isArray(jsonResponse.data.items)) {
        rawMaterialsArray = jsonResponse.data.items;
        paginationData = jsonResponse.data.pagination;
      }
    }

    return {
      rawMaterials: rawMaterialsArray,
      pagination: paginationData,
    };
  }

  /**
   * Get raw material by ID
   */
  async getRawMaterialById(id: number): Promise<RawMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/raw-materials/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Matéria-prima não encontrada');
      }
      throw new Error('Falha ao buscar matéria-prima');
    }

    const data: ApiResponse<RawMaterial> = await response.json();
    return data.data!;
  }

  /**
   * Validate code availability
   */
  async validateCode(code: string): Promise<{ available: boolean; message: string }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/raw-materials/validate-code', { code });

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error('Código é obrigatório');
      }
      throw new Error('Erro ao validar código');
    }

    const data: ApiResponse<{ available: boolean; message: string }> = await response.json();
    return data.data!;
  }

  /**
   * Create raw material
   */
  async createRawMaterial(data: RawMaterialFormData): Promise<RawMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/raw-materials', data);

    if (!response.ok) {
      if (response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Código '${data.code}' já está em uso. Por favor, utilize um código único.`);
      }
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Dados inválidos');
      }
      throw new Error('Falha ao criar matéria-prima');
    }

    const result: ApiResponse<RawMaterial> = await response.json();
    return result.data!;
  }

  /**
   * Update raw material
   */
  async updateRawMaterial(id: number, data: Partial<RawMaterialFormData>): Promise<RawMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/raw-materials/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Matéria-prima não encontrada');
      }
      if (response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Código já está em uso. Por favor, utilize um código único.`);
      }
      throw new Error('Falha ao atualizar matéria-prima');
    }

    const result: ApiResponse<RawMaterial> = await response.json();
    return result.data!;
  }

  /**
   * Delete raw material
   */
  async deleteRawMaterial(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/raw-materials/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Matéria-prima não encontrada');
      }
      if (response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Matéria-prima está vinculada a outros registros e não pode ser excluída');
      }
      throw new Error('Falha ao excluir matéria-prima');
    }
  }
}

// Singleton instance
export const rawMaterialService = new RawMaterialService();
export default rawMaterialService;
